import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useLocale, AdminLocale } from '../../hooks/useLocale';

const LOCALE_FLAGS: Record<AdminLocale, string> = {
    fr: '🇫🇷',
    en: '🇬🇧',
};

export default function UserLocaleSwitcher() {
    const { t } = useTranslation('navigation');
    const { locale, changeLocale, supportedLocales } = useLocale();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [open]);

    const handleSelect = async (next: AdminLocale) => {
        await changeLocale(next);
        setOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
                <GlobeAltIcon className="w-4 h-4 text-gray-400" />
                <span className="flex-1 text-left">{t('user_menu.language', 'Langue')}</span>
                <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase">
                    <span className="text-base leading-none">{LOCALE_FLAGS[locale]}</span>
                    {locale}
                </span>
            </button>

            {open && (
                <div className="absolute left-full ml-1 top-0 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[60] min-w-[160px]">
                    {supportedLocales.map((lang) => (
                        <button
                            key={lang}
                            type="button"
                            onClick={() => handleSelect(lang)}
                            className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <span className="text-base leading-none">{LOCALE_FLAGS[lang]}</span>
                                {t(`user_menu.languages.${lang}`, lang.toUpperCase())}
                            </span>
                            {lang === locale && <CheckIcon className="w-4 h-4 text-main-color" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
